import { AxiosError } from 'axios';
import { useFormik } from 'formik';
import { useCallback, useEffect, useState } from 'react';
import { useTimer } from 'react-timer-hook';

import useTriggeredRequest from '@api/hooks/useTriggeredRequest';
import AccountClientRequests from '@api/requests';

import { getResetPasswordDetails } from '@app/redux/auth/auth.selectors';
import { getBranchEnvironment } from '@app/redux/branchEnvironment/branchEnvironment.selectors';
import { useAppSelector } from '@app/redux/hooks';

import { ResetPasswordFormProps, ResetPasswordFormValues } from './interfaces';
import resetPasswordFormValidationScheme from './validationScheme';

const TIMER_MINUTES = 30;
const MILLISECONDS_IN_SECOND = 1000;
const SECONDS_IN_MINUTE = 60;
const TIMER_DURATION_MS = TIMER_MINUTES * SECONDS_IN_MINUTE * MILLISECONDS_IN_SECOND;

function useResetPasswordForm({
	initialValues,
	onSubmit,
	sentTime,
}: Pick<ResetPasswordFormProps, 'initialValues' | 'onSubmit' | 'sentTime'>) {
	const resetPasswordDetails = useAppSelector(getResetPasswordDetails);
	const branchEnvironment = useAppSelector(getBranchEnvironment);

	const [showPassword, setShowPassword] = useState(false);
	const [showPasswordConfirmation, setShowPasswordConfirmation] = useState(false);

	const handleShowPassword = () => setShowPassword(!showPassword);
	const handleShowPasswordConfirmation = () => setShowPasswordConfirmation(!showPasswordConfirmation);

	const { seconds, minutes, restart } = useTimer({
		expiryTimestamp: new Date(sentTime + TIMER_DURATION_MS),
	});

	const { startRequest } = useTriggeredRequest(AccountClientRequests.Auth.sendResetPasswordCode);

	const formik = useFormik<ResetPasswordFormValues>({
		initialValues,
		validationSchema: resetPasswordFormValidationScheme,
		onSubmit,
	});

	useEffect(
		() => restart(new Date(sentTime + TIMER_DURATION_MS)),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[resetPasswordDetails?.sentTime],
	);

	const handleResendCode = useCallback(async () => {
		try {
			formik.setFieldError('resendCodeError', '');

			await startRequest({
				auth_form_slug: branchEnvironment.auth_form_slug || '',
				code_type: resetPasswordDetails.code_type,
				contact: resetPasswordDetails.contact || '',
				reset_type: resetPasswordDetails.reset_type,
			});
		} catch (e) {
			const { response } = e as AxiosError<{
				errors?: {
					description?: string;
					field?: string;
				}[];
			}>;

			const fallbackMessage = 'Oops. Something went wrong.';

			const errorDescription = response?.data?.errors?.[0]?.description || fallbackMessage;

			formik.setFieldError(response?.data?.errors?.[0]?.field || 'resetPasswordError', errorDescription);
		}
	}, [
		branchEnvironment.auth_form_slug,
		formik,
		resetPasswordDetails.code_type,
		resetPasswordDetails.contact,
		resetPasswordDetails.reset_type,
		startRequest,
	]);

	return {
		showPassword,
		showPasswordConfirmation,
		handleShowPassword,
		handleShowPasswordConfirmation,
		handleResendCode,
		minutes,
		seconds,
		formik,
	};
}

export default useResetPasswordForm;
